// Глобальный сервис для управления уведомлениями
import type { Notification } from '../types';

const NOTIFICATIONS_STORAGE_KEY = 'level-sport-notifications';
const MAX_NOTIFICATIONS = 3;

// IndexedDB
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('level-sport-club', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('notifications')) {
        db.createObjectStore('notifications', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function countIndexedDB(): Promise<number> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('notifications', 'readonly');
      const req = tx.objectStore('notifications').count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    return 0;
  }
}

async function loadFromIndexedDB(): Promise<Notification[]> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('notifications', 'readonly');
      const req = tx.objectStore('notifications').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    return [];
  }
}

async function clearIndexedDB() {
  try {
    const db = await openDB();
    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction('notifications', 'readwrite');
      tx.objectStore('notifications').clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('[Notifications] Failed to clear IndexedDB:', error);
  }
}

// Загрузка уведомлений из localStorage
export function loadNotificationsFromStorage(): Notification[] {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (stored) {
      const notifications = JSON.parse(stored);
      return notifications.slice(0, MAX_NOTIFICATIONS);
    }
  } catch (error) {
    console.error('[NotificationService] Error loading from localStorage:', error);
  }
  return [];
}

// Сохранение уведомлений в localStorage
export function saveNotificationsToStorage(notifications: Notification[]) {
  try {
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(notifications));
  } catch (error) {
    console.error('[NotificationService] Error saving to localStorage:', error);
  }
}

// Добавить новое уведомление
export function addNotification(notification: Notification) {
  const current = loadNotificationsFromStorage();
  const updated = [notification, ...current].slice(0, MAX_NOTIFICATIONS);
  saveNotificationsToStorage(updated);

  // Уведомляем всех слушателей об изменении
  window.dispatchEvent(new CustomEvent('notificationsUpdated', {
    detail: { notifications: updated }
  }));

  return updated;
}

// Пометить как прочитанное
export function markAsRead(id: string) {
  const current = loadNotificationsFromStorage();
  const updated = current.map(n => n.id === id ? { ...n, isRead: true } : n).slice(0, MAX_NOTIFICATIONS);
  saveNotificationsToStorage(updated);

  window.dispatchEvent(new CustomEvent('notificationsUpdated', {
    detail: { notifications: updated }
  }));

  return updated;
}

// Пометить все как прочитанные
export function markAllAsRead() {
  const current = loadNotificationsFromStorage();
  const updated = current.map(n => ({ ...n, isRead: true })).slice(0, MAX_NOTIFICATIONS);
  saveNotificationsToStorage(updated);

  window.dispatchEvent(new CustomEvent('notificationsUpdated', {
    detail: { notifications: updated }
  }));

  return updated;
}

let channelInitialized = false;
let pollInterval: number | null = null;

async function checkAndLoadFromIndexedDB() {
  const count = await countIndexedDB();
  if (count === 0) return;

  const indexedDBNotifs = await loadFromIndexedDB();
  if (indexedDBNotifs.length > 0) {
    const current = loadNotificationsFromStorage();
    const existingIds = new Set(current.map(n => n.id));
    const newNotifs = indexedDBNotifs.filter(n => !existingIds.has(n.id));

    if (newNotifs.length > 0) {
      const updated = [...newNotifs, ...current].slice(0, MAX_NOTIFICATIONS);
      saveNotificationsToStorage(updated);
      window.dispatchEvent(new CustomEvent('notificationsUpdated', {
        detail: { notifications: updated }
      }));
    }

    await clearIndexedDB();
  }
}

export async function initNotificationListener() {
  if (channelInitialized) {
    return;
  }

  await checkAndLoadFromIndexedDB();

  // Периодическая проверка IndexedDB каждую минуту когда приложение открыто
  if (!document.hidden) {
    pollInterval = window.setInterval(checkAndLoadFromIndexedDB, 60000);
  }

  window.addEventListener('focus', checkAndLoadFromIndexedDB);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (pollInterval !== null) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    } else {
      checkAndLoadFromIndexedDB();
      if (pollInterval === null) {
        pollInterval = window.setInterval(checkAndLoadFromIndexedDB, 60000);
      }
    }
  });

  channelInitialized = true;

  (window as any).testNotification = () => {
    const testNotif = {
      id: Date.now().toString(),
      type: 'test',
      title: 'Тестовое уведомление',
      message: 'Проверка работы уведомлений',
      actionUrl: '/',
      isRead: false,
      createdAt: new Date().toISOString()
    };
    addNotification(testNotif);
  };
}
